import React, { useEffect, useMemo, useState } from 'react';
import ChildCareRoundedIcon from '@mui/icons-material/ChildCareRounded';
import MovieFilterRoundedIcon from '@mui/icons-material/MovieFilterRounded';
import NewspaperRoundedIcon from '@mui/icons-material/NewspaperRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import TheaterComedyRoundedIcon from '@mui/icons-material/TheaterComedyRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import WhatshotRoundedIcon from '@mui/icons-material/WhatshotRounded';
import UserPanelCategoryCarousel from './UserPanelCategoryCarousel';
import UserPanelContentSkeleton from './UserPanelContentSkeleton';
import { matchesTurkishSearch, normalizeTurkishSearchText } from './utils/turkishSearch';
import './userPanelWebTV.css';

const LOGO_ROOT = 'https://logo.xtreamcodes.net/uploads/tv_cihaz';
const logo = (fileName) => `${LOGO_ROOT}/${fileName}`;

const CATEGORY_IMAGES = Object.freeze({
  sports: '/assets/images/categories/cristiano-ronaldo-sport-clean-v3.png',
  cinema: '/assets/images/categories/cinema-action.jpg',
  documentary: '/assets/images/categories/documentary-fox.jpg',
  national: '/assets/images/categories/national-istanbul.jpg',
  kids: '/assets/images/showcase/disney-mandalorian.jpg',
  adult: '/assets/images/categories/adult-neon.jpg',
  exxen: '/assets/images/showcase/exxen-gibi.jpg',
  turkishSeries: '/assets/images/showcase/gain-modern-kadin.jpg',
  foreignSeries: '/assets/images/showcase/netflix-wednesday.jpg',
  disney: '/assets/images/showcase/disney-loki.jpg',
  amazon: '/assets/images/showcase/amazon-fallout.jpg',
  hbo: '/assets/images/showcase/hbo-house-of-the-dragon.jpg',
  gain: '/assets/images/showcase/gain-modern-kadin.jpg',
  tod: '/assets/images/showcase/tod-sorgu.jpg',
  entertainment: '/assets/images/showcase/exxen-sadece-arkadasiz.jpg',
});

export const resolveWebTvCategoryImage = (name) => {
  const normalized = normalizeTurkishSearchText(name);
  const includesAny = (...terms) => terms.some((term) => normalized.includes(term));

  if (includesAny('spor', 'sport', 'futbol', 'basketbol', 'nba')) return CATEGORY_IMAGES.sports;
  if (includesAny('belgesel', 'documentary', 'discovery', 'nature', 'nat geo')) return CATEGORY_IMAGES.documentary;
  if (includesAny('exxen')) return CATEGORY_IMAGES.exxen;
  if (includesAny('netflix')) return CATEGORY_IMAGES.foreignSeries;
  if (includesAny('disney')) return CATEGORY_IMAGES.disney;
  if (includesAny('amazon', 'prime video')) return CATEGORY_IMAGES.amazon;
  if (includesAny('hbo', 'max')) return CATEGORY_IMAGES.hbo;
  if (includesAny('gain')) return CATEGORY_IMAGES.gain;
  if (includesAny('tod')) return CATEGORY_IMAGES.tod;
  if (includesAny('cocuk', 'kids', 'cartoon', 'animasyon')) return CATEGORY_IMAGES.kids;
  if (includesAny('yetiskin', 'adult', 'erotik')) return CATEGORY_IMAGES.adult;
  if (includesAny('dizi', 'series')) {
    return includesAny('yabanci', 'foreign', 'fr:', 'de:', 'us:', 'uk:')
      ? CATEGORY_IMAGES.foreignSeries
      : CATEGORY_IMAGES.turkishSeries;
  }
  if (includesAny('sinema', 'film', 'movie', 'marvel', 'filmbox', 'housefilm')) return CATEGORY_IMAGES.cinema;
  if (includesAny('eglence', 'show', 'talk', 'muzik', 'music')) return CATEGORY_IMAGES.entertainment;
  if (includesAny('haber', 'news', 'ulusal', 'yerel', 'national', 'populer', 'tumu')) return CATEGORY_IMAGES.national;

  return CATEGORY_IMAGES.national;
};

const WEBTV_CHANNELS = Object.freeze([
  { id: '9', name: 'HD: beIN Sports 1', logo: logo('1637087123.png'), categories: ['popular', 'sports'] },
  { id: '91015', name: '4K: beIN Sports 1', logo: logo('1637010261.png'), categories: ['popular', 'sports'] },
  { id: '10', name: 'HD: beIN Sports 2', logo: logo('1637087138.png'), categories: ['sports'] },
  { id: '11', name: 'HD: beIN Sports 3', logo: logo('1637087151.png'), categories: ['sports'] },
  { id: '47', name: 'HD: S SPORTS 1', logo: logo('1637087209.png'), categories: ['popular', 'sports'] },
  { id: '50', name: 'HD: S SPORTS 2', logo: logo('1637087214.png'), categories: ['sports'] },
  { id: '43', name: 'HD: SPORT SMART', logo: logo('1637087253.png'), categories: ['popular', 'sports'] },
  { id: '45', name: 'HD: SPORT SMART 2', logo: logo('1637087259.png'), categories: ['sports'] },
  { id: '36', name: 'HD: TİVİBU SPOR 1', logo: logo('1637088922.png'), categories: ['sports'] },
  { id: '56', name: 'HD: A SPOR', logo: logo('1637087559.png'), categories: ['sports'] },
  { id: '261', name: 'HD: HISTORY', logo: logo('1637186550.png'), categories: ['popular', 'documentary'] },
  { id: '257', name: 'HD: NAT GEO', logo: logo('nat-geo.png'), categories: ['popular', 'documentary'] },
  { id: '268', name: 'HD: DISCOVERY IDX', logo: logo('1637186514.png'), categories: ['documentary'] },
  { id: '262', name: 'HD: VIASAT HISTORY', logo: logo('1637186522.png'), categories: ['documentary'] },
  { id: '276', name: 'HD: LOVE NATURE', logo: logo('1637186558.png'), categories: ['documentary'] },
  { id: '43970', name: 'HD: EPIC DRAMA', logo: logo('epicdrama.png'), categories: ['cinema', 'entertainment'] },
  { id: '183', name: 'HD: CNN TÜRK', logo: logo('1637095179.png'), categories: ['popular', 'news'] },
  { id: '74', name: 'HD: NOW TV', logo: logo('1637091350.png'), categories: ['popular', 'national', 'entertainment'] },
]);

const CATEGORY_DEFINITIONS = Object.freeze([
  { id: 'sports', label: 'Spor', image: resolveWebTvCategoryImage('Spor'), icon: SportsSoccerRoundedIcon },
  { id: 'cinema', label: 'Sinema', image: resolveWebTvCategoryImage('Sinema'), icon: MovieFilterRoundedIcon },
  { id: 'documentary', label: 'Belgesel', image: resolveWebTvCategoryImage('Belgesel'), icon: TravelExploreRoundedIcon },
  { id: 'national', label: 'Ulusal', image: resolveWebTvCategoryImage('Ulusal'), icon: TvRoundedIcon },
  { id: 'news', label: 'Haber', image: resolveWebTvCategoryImage('Haber'), icon: NewspaperRoundedIcon },
  { id: 'kids', label: 'Çocuk', image: resolveWebTvCategoryImage('Çocuk'), icon: ChildCareRoundedIcon },
  { id: 'popular', label: 'Popüler', image: resolveWebTvCategoryImage('Popüler'), icon: WhatshotRoundedIcon },
  { id: 'entertainment', label: 'Eğlence', image: resolveWebTvCategoryImage('Eğlence'), icon: TheaterComedyRoundedIcon },
]);

const resolveCategoryIcon = (name) => {
  const normalized = String(name || '').toLocaleLowerCase('tr-TR');
  if (normalized.includes('spor')) return SportsSoccerRoundedIcon;
  if (normalized.includes('haber')) return NewspaperRoundedIcon;
  if (normalized.includes('belgesel')) return TravelExploreRoundedIcon;
  if (normalized.includes('çocuk') || normalized.includes('cocuk')) return ChildCareRoundedIcon;
  if (normalized.includes('sinema') || normalized.includes('film')) return MovieFilterRoundedIcon;
  if (normalized.includes('eğlence') || normalized.includes('eglence')) return TheaterComedyRoundedIcon;
  return TvRoundedIcon;
};

const getWebTvCategoryId = (category) => String(category.id ?? category.category_id);

const getWebTvCategoryLabel = (category) => String(
  category.label || category.name || category.category_name || 'Canlı TV'
);

const isCinemaCategory = (label) => {
  const normalized = normalizeTurkishSearchText(label);
  return normalized === 'sinema' || normalized.startsWith('sinema ');
};

export const buildWebTvCategoryDefinitions = (categories) => {
  const definitions = categories.map((category) => {
    const id = getWebTvCategoryId(category);
    const label = getWebTvCategoryLabel(category);

    return {
      ...category,
      id,
      label,
      image: category.image || resolveWebTvCategoryImage(label),
      icon: category.icon || resolveCategoryIcon(label),
      sourceCategoryIds: [id],
    };
  });
  const cinemaCategoryIds = definitions
    .filter((category) => isCinemaCategory(category.label))
    .map((category) => category.id);

  if (cinemaCategoryIds.length === 0) return definitions;

  let cinemaCategoryAdded = false;
  return definitions.reduce((groupedCategories, category) => {
    if (!isCinemaCategory(category.label)) {
      groupedCategories.push(category);
      return groupedCategories;
    }
    if (cinemaCategoryAdded) return groupedCategories;

    cinemaCategoryAdded = true;
    groupedCategories.push({
      ...category,
      label: 'Sinema',
      image: resolveWebTvCategoryImage('Sinema'),
      icon: MovieFilterRoundedIcon,
      sourceCategoryIds: cinemaCategoryIds,
    });
    return groupedCategories;
  }, []);
};

const categoryContainsChannel = (category, channel) => (
  category.sourceCategoryIds.some((categoryId) => channel.categories.includes(categoryId))
);

const getChannelDisplayName = (name) => String(name || '')
  .replace(/^\s*(4K|FHD|HD|SD)\s*:\s*/i, '')
  .trim();

const getInitials = (name) => getChannelDisplayName(name)
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part.charAt(0))
  .join('')
  .toLocaleUpperCase('tr-TR');

function ChannelLogo({ channel, compact = false, watermark = false }) {
  const [logoFailed, setLogoFailed] = useState(false);

  return (
    <span className={`tvup-webtv-logo${compact ? ' is-compact' : ''}${watermark ? ' is-watermark' : ''}`} aria-hidden="true">
      {(logoFailed || !channel.logo) && <span>{getInitials(channel.name)}</span>}
      {!logoFailed && channel.logo && (
        <img
          src={channel.logo}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setLogoFailed(true)}
        />
      )}
    </span>
  );
}

function UserPanelWebTV({ onOpenWindowsModal, onChannelSelect, channels = WEBTV_CHANNELS, categories, loading = false }) {
  const [activeCategory, setActiveCategory] = useState('sports');
  const [query, setQuery] = useState('');

  const sourceCategories = useMemo(
    () => (Array.isArray(categories) ? categories : CATEGORY_DEFINITIONS),
    [categories]
  );
  const categoryDefinitions = useMemo(
    () => buildWebTvCategoryDefinitions(sourceCategories),
    [sourceCategories]
  );

  const normalizedChannels = useMemo(() => channels.map((channel) => {
    const channelCategories = Array.isArray(channel.categories) && channel.categories.length
      ? channel.categories.map(String)
      : [String(channel.categoryId ?? channel.category_id ?? '')];
    const categoryName = channel.categoryName
      || channel.category_name
      || getWebTvCategoryLabel(sourceCategories.find(
        (category) => channelCategories.includes(getWebTvCategoryId(category))
      ) || {})
      || 'Canlı TV';

    return {
      ...channel,
      id: String(channel.id ?? channel.stream_id),
      name: channel.name || channel.stream_display_name || 'Kanal',
      logo: channel.logo || channel.stream_icon || '',
      categories: channelCategories,
      categoryName,
    };
  }), [channels, sourceCategories]);

  useEffect(() => {
    if (categoryDefinitions.some((category) => category.id === activeCategory)) return;
    setActiveCategory(categoryDefinitions[0]?.id || '');
  }, [activeCategory, categoryDefinitions]);

  const categoryCounts = useMemo(() => categoryDefinitions.reduce((counts, category) => {
    counts[category.id] = normalizedChannels.filter((channel) => categoryContainsChannel(category, channel)).length;
    return counts;
  }, {}), [categoryDefinitions, normalizedChannels]);

  const activeCategoryDefinition = categoryDefinitions.find((category) => category.id === activeCategory);

  const filteredChannels = useMemo(() => {
    const normalizedQuery = normalizeTurkishSearchText(query);

    return normalizedChannels.filter((channel) => {
      const categoryMatches = Boolean(normalizedQuery)
        || (activeCategoryDefinition && categoryContainsChannel(activeCategoryDefinition, channel));
      const queryMatches = matchesTurkishSearch(getChannelDisplayName(channel.name), normalizedQuery);
      return categoryMatches && queryMatches;
    });
  }, [activeCategoryDefinition, normalizedChannels, query]);

  const activeCategoryLabel = activeCategoryDefinition?.label || 'Canlı TV';
  const hasActiveSearch = query.trim().length > 0;

  const selectCategory = (categoryId) => {
    setActiveCategory(categoryId);
    setQuery('');
  };

  const handleCategoryKeyDown = (event, index) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    const lastIndex = categoryDefinitions.length - 1;
    const categoryCount = categoryDefinitions.length;
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? lastIndex
        : event.key === 'ArrowRight'
          ? (index + 1) % categoryCount
          : (index - 1 + categoryCount) % categoryCount;

    selectCategory(categoryDefinitions[nextIndex].id);
    const buttons = event.currentTarget.parentElement?.querySelectorAll('[role="tab"]');
    buttons?.[nextIndex]?.focus();
  };

  if (loading && normalizedChannels.length === 0) {
    return <UserPanelContentSkeleton variant="webtv" />;
  }

  return (
    <div className="tvup-webtv-page">
      <section className="tvup-webtv-category-section" aria-labelledby="tvup-webtv-category-title">
        <div className="tvup-webtv-section-heading">
          <div>
            <h2 id="tvup-webtv-category-title">Canlı TV’de Ne İzlemek İstersin?</h2>
          </div>
          <p>Kanal listesini ilgi alanına göre daralt.</p>
        </div>

        <UserPanelCategoryCarousel
          id="tvup-webtv-category-tabs"
          className="tvup-webtv-categories"
          ariaLabel="Kanal kategorileri"
          previousLabel="Canlı TV kategorilerini sola kaydır"
          nextLabel="Canlı TV kategorilerini sağa kaydır"
        >
          {categoryDefinitions.map((category, index) => {
            const CategoryIcon = category.icon;
            const isActive = activeCategory === category.id;
            return (
              <button
                key={category.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls="tvup-webtv-channel-panel"
                tabIndex={isActive ? 0 : -1}
                className={`${isActive ? 'is-active ' : ''}tone-${category.id}`.trim()}
                onClick={() => selectCategory(category.id)}
                onKeyDown={(event) => handleCategoryKeyDown(event, index)}
              >
                {category.image && <img src={category.image} alt="" loading="lazy" />}
                <span className="tvup-webtv-category-shade" />
                <span className="tvup-webtv-category-icon"><CategoryIcon /></span>
                <span className="tvup-webtv-category-copy"><small>{category.label}</small><b>{categoryCounts[category.id]} kanal</b></span>
              </button>
            );
          })}
        </UserPanelCategoryCarousel>
      </section>

      <section
        id="tvup-webtv-channel-panel"
        className="tvup-webtv-channel-section"
        role="tabpanel"
        aria-labelledby="tvup-webtv-category-title"
      >
        <div className="tvup-webtv-channel-toolbar">
          <div>
            <h2 id="tvup-webtv-channel-title">{hasActiveSearch ? 'Kanal Arama Sonuçları' : activeCategoryLabel}</h2>
            <p>{filteredChannels.length} kanal gösteriliyor</p>
          </div>
          <label className="tvup-webtv-search">
            <SearchRoundedIcon aria-hidden="true" />
            <input
              type="search"
              value={query}
              aria-label="Kanal ara"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
        </div>

        {filteredChannels.length > 0 ? (
          <div className="tvup-webtv-channel-grid">
            {filteredChannels.map((channel) => (
              <button
                key={channel.id}
                type="button"
                className="tvup-webtv-channel-card"
                aria-haspopup="dialog"
                aria-controls="tvup-player-modal"
                onClick={() => {
                  if (typeof onChannelSelect === 'function') onChannelSelect(channel);
                  else onOpenWindowsModal?.(channel);
                }}
              >
                <ChannelLogo channel={channel} watermark />
                <span className="tvup-webtv-channel-copy">
                  <b>{getChannelDisplayName(channel.name)}</b>
                  <small>{channel.categoryName || activeCategoryLabel}</small>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="tvup-webtv-empty" role="status">
            <SearchRoundedIcon />
            <b>Bu aramayla eşleşen kanal bulunamadı.</b>
            <span>Farklı bir kanal adı deneyebilir veya kategoriyi değiştirebilirsin.</span>
          </div>
        )}
      </section>
    </div>
  );
}

export default UserPanelWebTV;
